import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardMedia,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  TextField,
  MenuItem,
  Stack,
  Pagination,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAuth } from '../../../contexts/AuthContext';
import ImageViewerDialog from '../ImageViewerDialog';
import { createConcurrencyLimiter } from '../../../utils/concurrentLimit';

interface DeviceEvent {
  id: string;
  deviceId: string;
  name: string;
  eventType: string;
  dateCreated: string;
}

interface SnapshotItem {
  event: DeviceEvent;
  thumbnailUrl: string;
}

interface SnapshotTabProps {
  deviceId: string;
  includeChildren?: boolean;
}

const PAGE_SIZE_OPTIONS = [25, 50, 100];

const toUtcIso = (value: string): string => {
  if (!value) return '';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '' : parsed.toISOString();
};

const SnapshotTab: React.FC<SnapshotTabProps> = ({ deviceId, includeChildren = false }) => {
  const auth = useAuth();
  const accessToken = auth.tokens?.accessToken;
  const [snapshots, setSnapshots] = useState<SnapshotItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const [totalEvents, setTotalEvents] = useState(0);

  // Viewer state
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerImages, setViewerImages] = useState<string[]>([]);
  const [viewerIndex, setViewerIndex] = useState(0);
  const [viewerTitle, setViewerTitle] = useState('');

  // Delete state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteAllConfirmOpen, setDeleteAllConfirmOpen] = useState(false);
  const [deletePageConfirmOpen, setDeletePageConfirmOpen] = useState(false);
  const [deletingPage, setDeletingPage] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<SnapshotItem | null>(null);

  const confirmDelete = (e: React.MouseEvent, item: SnapshotItem) => {
    e.stopPropagation();
    setItemToDelete(item);
    setDeleteConfirmOpen(true);
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;
    const dId = itemToDelete.event.deviceId || deviceId;
    try {
      const res = await fetch(`/api/devices/${dId}/events/${itemToDelete.event.id}`, {
        method: 'DELETE', headers: authHeader,
      });
      if (!res.ok) throw new Error('Delete failed');
      URL.revokeObjectURL(itemToDelete.thumbnailUrl);
      setSnapshots(prev => prev.filter(s => s.event.id !== itemToDelete.event.id));
    } catch {
      setError('Failed to delete snapshot');
    } finally {
      setDeleteConfirmOpen(false);
      setItemToDelete(null);
    }
  };

  const handleDeleteAll = async () => {
    try {
      const res = await fetch(`/api/devices/${deviceId}/events/all?eventType=snapshot`, {
        method: 'DELETE', headers: authHeader,
      });
      if (!res.ok) throw new Error('Delete all failed');
      snapshots.forEach(s => URL.revokeObjectURL(s.thumbnailUrl));
      setSnapshots([]);
      setTotalEvents(0);
    } catch {
      setError('Failed to delete all snapshots');
    } finally {
      setDeleteAllConfirmOpen(false);
    }
  };

  const handleDeletePage = async () => {
    setDeletingPage(true);
    try {
      const limitRequests = createConcurrencyLimiter(6);
      const results = await Promise.allSettled(
        snapshots.map((item) => limitRequests(async () => {
          const dId = item.event.deviceId || deviceId;
          const res = await fetch(`/api/devices/${dId}/events/${item.event.id}`, {
            method: 'DELETE', headers: authHeader,
          });
          if (!res.ok) throw new Error('Delete failed');
        }))
      );
      const failedCount = results.filter((r) => r.status === 'rejected').length;
      if (failedCount > 0) {
        setError(`Failed to delete ${failedCount} of ${snapshots.length} snapshot(s)`);
      }
      await loadSnapshots();
    } catch {
      setError('Failed to delete this page of snapshots');
    } finally {
      setDeletingPage(false);
      setDeletePageConfirmOpen(false);
    }
  };

  const authHeader = { Authorization: `Bearer ${accessToken}` };

  const loadSnapshots = useCallback(async () => {
    setLoading(true);
    setError(null);
    setSnapshots([]);

    try {
      const skip = (page - 1) * pageSize;
      const params = new URLSearchParams({
        skip: skip.toString(),
        take: pageSize.toString(),
        sortBy: 'dateCreated',
        sortOrder: 'desc',
        eventType: 'snapshot',
        hasImage: 'true',
      });
      const startDateUtc = toUtcIso(startDate);
      const endDateUtc = toUtcIso(endDate);
      if (startDateUtc) params.append('startDate', startDateUtc);
      if (endDateUtc) params.append('endDate', endDateUtc);
      if (includeChildren) params.append('includeChildren', 'true');

      const res = await fetch(`/api/devices/${deviceId}/events?${params}`, {
        headers: authHeader,
      });
      if (!res.ok) throw new Error('Failed to load events');
      const data = await res.json();
      const events: DeviceEvent[] = data.data || [];
      setTotalEvents(data.total || 0);

      // Load thumbnails with limited concurrency to avoid overwhelming the API
      const limitRequests = createConcurrencyLimiter(8);
      const results = await Promise.allSettled(
        events.map((ev) => limitRequests(async () => {
          const dId = ev.deviceId || deviceId;
          const r = await fetch(
            `/api/devices/${dId}/events/${ev.id}/latest-image/thumbnail?maxWidth=200`,
            { headers: authHeader }
          );
          if (!r.ok) return null;
          const blob = await r.blob();
          return { event: ev, thumbnailUrl: URL.createObjectURL(blob) } as SnapshotItem;
        }))
      );

      const items = results
        .filter((r): r is PromiseFulfilledResult<SnapshotItem | null> => r.status === 'fulfilled')
        .map((r) => r.value)
        .filter((v): v is SnapshotItem => v !== null);

      setSnapshots(items);
    } catch (e: any) {
      setError(e.message || 'Failed to load snapshots');
    } finally {
      setLoading(false);
    }
  }, [deviceId, page, pageSize, startDate, endDate, includeChildren]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadSnapshots();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, startDate, endDate, includeChildren, deviceId]);

  // Revoke blob URLs on unmount / re-load
  useEffect(() => {
    return () => {
      snapshots.forEach((s) => URL.revokeObjectURL(s.thumbnailUrl));
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshots]);

  const handleCardClick = async (item: SnapshotItem) => {
    const dId = item.event.deviceId || deviceId;
    setViewerTitle(`${item.event.name || item.event.eventType} — ${new Date(item.event.dateCreated).toLocaleString()}`);
    setViewerImages([item.thumbnailUrl]); // show thumb immediately while loading full images
    setViewerIndex(0);
    setViewerOpen(true);

    try {
      const r = await fetch(
        `/api/devices/${dId}/events/${item.event.id}/images?skip=0&take=20`,
        { headers: authHeader }
      );
      if (!r.ok) return;
      const imgs: { id: string }[] = await r.json();

      const fullUrls: string[] = [];
      for (const img of imgs) {
        const fr = await fetch(
          `/api/devices/${dId}/events/${item.event.id}/images/${img.id}/content?maxWidth=1200`,
          { headers: authHeader }
        );
        if (fr.ok) {
          const b = await fr.blob();
          fullUrls.push(URL.createObjectURL(b));
        }
      }
      if (fullUrls.length > 0) {
        setViewerImages(fullUrls);
      }
    } catch {
      // stay on thumbnail
    }
  };

  const handleViewerClose = () => {
    viewerImages.forEach((u) => {
      if (!snapshots.some((s) => s.thumbnailUrl === u)) {
        URL.revokeObjectURL(u);
      }
    });
    setViewerOpen(false);
    setViewerImages([]);
  };

  const pageCount = Math.max(1, Math.ceil(totalEvents / pageSize));

  return (
    <Box>
      {/* Filters */}
      <Stack direction="row" spacing={2} sx={{ mb: 3 }} flexWrap="wrap" alignItems="center">
        <TextField
          label="From"
          type="datetime-local"
          size="small"
          InputLabelProps={{ shrink: true }}
          value={startDate}
          onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
        />
        <TextField
          label="To"
          type="datetime-local"
          size="small"
          InputLabelProps={{ shrink: true }}
          value={endDate}
          onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
        />
        <TextField
          select
          label="Per page"
          size="small"
          value={pageSize}
          onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
          sx={{ minWidth: 110 }}
        >
          {PAGE_SIZE_OPTIONS.map((size) => (
            <MenuItem key={size} value={size}>{size}</MenuItem>
          ))}
        </TextField>
        <Box sx={{ flexGrow: 1 }} />
        {snapshots.length > 0 && (
          <Button
            size="small"
            color="error"
            variant="outlined"
            startIcon={<DeleteIcon />}
            onClick={() => setDeletePageConfirmOpen(true)}
          >
            Delete Page
          </Button>
        )}
        {snapshots.length > 0 && (
          <Button
            size="small"
            color="error"
            variant="outlined"
            startIcon={<DeleteIcon />}
            onClick={() => setDeleteAllConfirmOpen(true)}
          >
            Delete All
          </Button>
        )}
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      ) : snapshots.length === 0 ? (
        <Typography color="text.secondary" textAlign="center" py={8}>
          No snapshots found for this period.
        </Typography>
      ) : (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Showing {snapshots.length} of {totalEvents} snapshot{totalEvents !== 1 ? 's' : ''}
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fill, minmax(125px, 1fr))' },
            }}
          >
            {snapshots.map((item) => (
              <Card
                key={item.event.id}
                sx={{
                  cursor: 'pointer',
                  position: 'relative',
                  transition: 'box-shadow 0.2s, transform 0.15s',
                  '&:hover': { boxShadow: 8, transform: 'scale(1.02)' },
                  '&:hover .delete-btn': { opacity: 1 },
                }}
                onClick={() => handleCardClick(item)}
              >
                <Tooltip title="Delete snapshot">
                  <IconButton
                    className="delete-btn"
                    size="small"
                    onClick={(e) => confirmDelete(e, item)}
                    sx={{
                      position: 'absolute', top: 4, right: 4, zIndex: 2,
                      opacity: 0, transition: 'opacity 0.2s',
                      bgcolor: 'rgba(0,0,0,0.55)', color: 'white',
                      '&:hover': { bgcolor: 'error.main' },
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <CardMedia
                  component="img"
                  image={item.thumbnailUrl}
                  alt={item.event.name || item.event.eventType}
                  sx={{ height: { xs: 220, sm: 125 }, objectFit: 'cover' }}
                />
                <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                  <Typography variant="caption" color="text.secondary" display="block" noWrap>
                    {new Date(item.event.dateCreated).toLocaleString()}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Box>

          {pageCount > 1 && (
            <Box display="flex" justifyContent="center" mt={4}>
              <Pagination
                count={pageCount}
                page={page}
                onChange={(_, v) => setPage(v)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}

      <ImageViewerDialog
        open={viewerOpen}
        onClose={handleViewerClose}
        imageUrl={viewerImages[viewerIndex] || ''}
        eventName={viewerTitle}
        currentIndex={viewerIndex}
        totalImages={viewerImages.length}
        onNext={viewerImages.length > 1 ? () => setViewerIndex((i) => (i + 1) % viewerImages.length) : undefined}
        onPrevious={viewerImages.length > 1 ? () => setViewerIndex((i) => (i - 1 + viewerImages.length) % viewerImages.length) : undefined}
      />

      {/* Delete single confirm */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Snapshot?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {itemToDelete && new Date(itemToDelete.event.dateCreated).toLocaleString()}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Delete all confirm */}
      <Dialog open={deleteAllConfirmOpen} onClose={() => setDeleteAllConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete All Snapshots?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">This will permanently delete all snapshots for this device. This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteAllConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteAll} color="error" variant="contained">Delete All</Button>
        </DialogActions>
      </Dialog>

      {/* Delete page confirm */}
      <Dialog open={deletePageConfirmOpen} onClose={() => setDeletePageConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete This Page?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This will permanently delete the {snapshots.length} snapshot{snapshots.length !== 1 ? 's' : ''} shown on this page. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletePageConfirmOpen(false)} disabled={deletingPage}>Cancel</Button>
          <Button onClick={handleDeletePage} color="error" variant="contained" disabled={deletingPage}>
            {deletingPage ? 'Deleting…' : 'Delete Page'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SnapshotTab;
